import { useEffect, useState } from 'react'
import ArenaSlot from '../components/ArenaSlot.jsx'
import ReadAloud from '../components/ReadAloud.jsx'
import { play } from '../lib/sounds.js'

/**
 * SPEC screen 5: one beat per screen, poses swap per beat, a lunge on the
 * fierce pose, tap anywhere to advance. Sound hooks fire per beat.
 *
 * Polish (step 9): the tap target is the whole screen, minus any real control —
 * a tap that lands on a button (the read-aloud, Back, Next) does that button's
 * job instead of skipping the beat. See `onSceneTap`.
 */
export default function Battle({ story, A, B, age, features, onBack, onDone }) {
  const [i, setI] = useState(0)
  const beats = story.battle
  const beat = beats[i]
  const last = i === beats.length - 1
  const prevBeat = beats[i - 1]

  useEffect(() => setI(0), [story])
  useEffect(() => {
    if (beat.sound) play((beat.sound === 'a' ? A : B).sound, { pose: beat.sound === 'a' ? beat.pose_a : beat.pose_b })
  }, [i]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Tap anywhere advances — except on a real control. Previously the whole text
   * block called stopPropagation so the read-aloud button would work, but on a
   * phone that block IS most of the screen, so the most natural place to tap did
   * nothing (D-071). Ignoring taps that land on a button is the narrow version of
   * the same guard.
   */
  const goNext = () => (last ? onDone() : setI(i + 1))
  /** Tap anywhere except a real control. The Next button calls goNext directly —
      routing it through here made it bail on its own guard. */
  const onSceneTap = (e) => {
    if (e?.target?.closest?.('button')) return
    goNext()
  }
  /** Back off the first beat leaves the battle for the fact pages (D-127): a
      greyed-out Back was the one place in the flow with no way backwards. */
  const back = () => (i === 0 ? onBack() : setI(i - 1))
  const lungeA = beat.pose_a === 'fierce' && prevBeat?.pose_a !== 'fierce'
  const lungeB = beat.pose_b === 'fierce' && prevBeat?.pose_b !== 'fierce'
  // The story's new word (SPEC 8) is defined wherever it first appears. The
  // templated fallback puts it in a battle beat, so the card belongs here too,
  // not only on the fact pages.
  const vocab = story.vocab_word
  const showsVocab = vocab && beat.text.toLowerCase().includes(vocab.word.toLowerCase())

  return (
    <section className="screen battle" onClick={onSceneTap} role="button" tabIndex={0} aria-label="Tap to continue"
      // Only a key press on the section itself advances; one on the Next button
      // already advances through its click, and would otherwise count twice.
      // Enter only: the space bar reads the beat aloud everywhere (D-112).
      onKeyDown={(e) => e.target === e.currentTarget && e.key === 'Enter' && goNext()}>
      <ArenaSlot features={features} a={A} b={B} poseA={beat.pose_a} poseB={beat.pose_b} setting={story.setting}
        badge={i === 0 ? 'VS' : null} lungeA={lungeA} lungeB={lungeB} cue={{ beats, index: i, winner: story.winner }} />
      <ReadAloud key={i} text={beat.text} auto={age !== '6'} className="battle__text" size="lg" />
      {showsVocab && (
        <div className="vocab-card">
          <span className="vocab-card__tag">New word</span>
          <strong>{vocab.word}</strong>
          <span>{vocab.definition}</span>
        </div>
      )}
      <div className="battle__foot">
        <button className="btn battle__back" onClick={back} aria-label="Back">◀ <span className="battle__back-label">Back</span></button>
        <div className="battle__beats" aria-label={`Beat ${i + 1} of ${beats.length}`}>
          {beats.map((_, n) => <span key={n} className={`battle__beat${n < i ? ' is-done' : n === i ? ' is-on' : ''}`} />)}
        </div>
        <button className="btn btn--primary battle__next" onClick={goNext}>
          {last ? 'Who wins? ▶' : 'Next ▶'}
        </button>
      </div>
    </section>
  )
}
